// Einhaengepunkt und Zugangszustand von apps/web (ST-UX-001).
//
// WAS SICH GEGENUEBER DEM DURCHSTICH GEAENDERT HAT: diese Datei war der ganze
// Bildschirm. Sie trug Anmeldung, Zugriffsentscheidung, technische Sicht und
// die Schnellerfassung in einem Stueck. Das war fuer EINEN Bildschirm richtig
// und wird mit dem zweiten falsch -- zwei Aufgaben untereinander auf einer
// Seite haben keine erkennbare Hauptaktion mehr (NFR-UX-004), keine Adresse je
// Aufgabe und keine Ueberschriftenstruktur, die sagt, wo man ist.
//
// Geblieben ist hier genau das, was die ganze Anwendung betrifft: die Anmeldung
// und das Wissen darueber, was das angemeldete Konto darf. Alles andere liegt in
// `shell/` (Rahmen) und `screens/` (Bildschirme).
//
// WAS ES WEITERHIN NICHT IST: eine Produktansicht. Es gibt zwei Bildschirme,
// weil es zwei Routen gibt. Eine Attrappe zu bauen waere in einer
// Teamvorstellung schaedlicher als ein schmaler, ehrlicher Rahmen -- sie erzeugt
// Erwartungen, die der Code nicht haelt.

import { StrictMode, useCallback, useEffect, useState, type ReactElement } from 'react';
import { createRoot } from 'react-dom/client';
import { FluentProvider } from '@fluentui/react-components';
import {
  InteractionRequiredAuthError,
  PublicClientApplication,
  type AccountInfo,
  type AuthenticationResult,
} from '@azure/msal-browser';

import { Spinner } from '@fluentui/react-components';

import { AccessScreen } from './screens/access.tsx';
import { AdressenScreen } from './screens/adressen.tsx';
import { KennungenScreen } from './screens/kennungen.tsx';
import { OrganisationScreen } from './screens/organisation.tsx';
import { KontaktScreen } from './screens/kontakt.tsx';
import { KontaktwegeScreen } from './screens/kontaktwege.tsx';
import { KontakteScreen } from './screens/kontakte.tsx';
import { WiedervorlagenScreen } from './screens/wiedervorlagen.tsx';
import { MeineExporteScreen } from './screens/meine-exporte.tsx';
import { TestversandScreen } from './screens/testversand.tsx';
import { SammlungenScreen } from './screens/sammlungen.tsx';
import { SammlungScreen } from './screens/sammlung.tsx';
import { MassenlaufScreen, MassenpflegeScreen } from './screens/massenpflege.tsx';
import { DatenqualitaetScreen } from './screens/datenqualitaet.tsx';
import { StichwortkatalogScreen } from './screens/stichwortkatalog.tsx';
import { RollenkatalogScreen } from './screens/rollenkatalog.tsx';
import { forgetBatchSelection } from './classification-batches.ts';
import { forgetSearchTerm } from './parties.ts';
import { forgetForeignNoteDrafts, forgetNoteDrafts } from './interactions.ts';
import { NotFoundScreen } from './screens/not-found.tsx';
import { QuickCaptureScreen } from './screens/quick-capture.tsx';
import { SignInScreen } from './screens/sign-in.tsx';
import { ScreenErrorBoundary } from './shell/error-boundary.tsx';
import { AppShell } from './shell/layout.tsx';
import { CHECKING_SCREEN, ROLES_UNKNOWN, SIGN_IN_SCREEN, resolve, type KnownRoles } from './shell/navigation.ts';
import { setzeAnmeldung } from './shell/anmeldung.ts';
import { Umleitung, usePath } from './shell/router.tsx';
import { fetchAccessSnapshot, type AccessSnapshot, type FetchLike } from './shell/session.ts';
import type { PlainMessage } from './access.ts';
import { usePrefersDark } from './shell/theme-preference.ts';
import { liaisonDarkTheme, liaisonLightTheme } from './theme.ts';

declare const __LIAISON_TENANT_ID__: string;
declare const __LIAISON_CLIENT_ID__: string;
declare const __LIAISON_API_SCOPE__: string;
declare const __LIAISON_API_BASE_URL__: string;
declare const __LIAISON_REDIRECT_URI__: string;

const msal = new PublicClientApplication({
  auth: {
    clientId: __LIAISON_CLIENT_ID__,
    authority: `https://login.microsoftonline.com/${__LIAISON_TENANT_ID__}`,
    redirectUri: __LIAISON_REDIRECT_URI__,
  },
  cache: {
    // sessionStorage und nicht localStorage: eine Anmeldung endet mit dem
    // Schliessen des Tabs. Auf einem geteilten Rechner ist das der Unterschied
    // zwischen einer Sitzung und einem dauerhaften Zugang.
    cacheLocation: 'sessionStorage',
  },
});

/**
 * Einmalige Initialisierung, auf Modulebene und nicht im Effekt.
 *
 * React fuehrt Effekte im StrictMode absichtlich ZWEIMAL aus. Zwei parallele
 * handleRedirectPromise()-Aufrufe brechen MSALs Redirect-Behandlung: der erste
 * verbraucht den Hash, der zweite findet keinen mehr und hinterlaesst einen
 * Zustand, in dem acquireTokenSilent scheitert. Genau das war der Fehler
 * "Deine Anmeldung ist nicht mehr gueltig" direkt nach der Anmeldung.
 *
 * Eine Zusage auf Modulebene wird genau einmal erzeugt; jeder weitere Aufruf
 * wartet auf dieselbe.
 */
let readyPromise: Promise<AuthenticationResult | null> | undefined;
function ready(): Promise<AuthenticationResult | null> {
  readyPromise ??= (async () => {
    await msal.initialize();
    return await msal.handleRedirectPromise();
  })();
  return readyPromise;
}

function App(): ReactElement {
  const [account, setAccount] = useState<AccountInfo | undefined>();
  const [snapshot, setSnapshot] = useState<AccessSnapshot | undefined>();
  const [busy, setBusy] = useState(true);
  const [authProblem, setAuthProblem] = useState<PlainMessage | undefined>();
  /** Ursache der Anmeldestoerung, nur fuer die technische Sicht. */
  const [authDetail, setAuthDetail] = useState<string | undefined>();
  const path = usePath();

  const load = useCallback(async (current: AccountInfo): Promise<void> => {
    setBusy(true);
    setAuthProblem(undefined);
    setAuthDetail(undefined);
    let accessToken: string;
    try {
      const result = await msal.acquireTokenSilent({ account: current, scopes: [__LIAISON_API_SCOPE__] });
      accessToken = result.accessToken;
    } catch (error) {
      // InteractionRequiredAuthError ist der VORGESEHENE Weg und kein Fehler:
      // MSAL sagt damit, dass es die Anmeldung nicht still erneuern kann. Der
      // Standardweg ist dann ein Redirect. Ich hatte das zuerst abgelehnt, weil
      // ein Dialog ohne Zutun wie ein Fehler wirkt -- das war falsch: direkt
      // nach einem Anmeldeklick ist die Weiterleitung erwartbar, und eine
      // Meldung mit zweitem Knopf ist dort die schlechtere Erfahrung.
      if (error instanceof InteractionRequiredAuthError) {
        await msal.acquireTokenRedirect({ account: current, scopes: [__LIAISON_API_SCOPE__] });
        return;
      }
      // Jeder andere Fehler behaelt seine Ursache. Sie verschwiegen zu haben
      // war der eigentliche Mangel der ersten Fassung: der Bildschirm sagte
      // "nicht mehr gueltig", und niemand konnte sagen, warum.
      setAuthProblem({
        headline: 'Deine Anmeldung konnte nicht verwendet werden.',
        explanation: 'LIAISON hat deine Anmeldung erhalten, konnte sie aber nicht verwenden.',
        nextStep: 'Melde dich neu an, dann geht es weiter.',
        retryable: true,
      });
      setAuthDetail(error instanceof Error ? `${error.name}: ${error.message}` : String(error));
      setSnapshot(undefined);
      setBusy(false);
      return;
    }

    // GETRENNT vom Anmeldeblock, und das ist der Punkt: die erste Fassung fing
    // beides in einem try. Ein gescheiterter Aufruf der API erschien damit als
    // "Deine Anmeldung konnte nicht verwendet werden" -- obwohl die Anmeldung
    // in Ordnung war. Genau so ist am 24.08.2026 eine CORS-Blockade eine
    // Stunde lang als Anmeldefehler gelesen worden.
    try {
      setSnapshot(await fetchAccessSnapshot(__LIAISON_API_BASE_URL__, accessToken, fetch as unknown as FetchLike));
    } catch (error) {
      setAuthProblem({
        headline: 'LIAISON ist gerade nicht erreichbar.',
        explanation: 'Deine Anmeldung war erfolgreich; der Aufruf der Anwendung nicht.',
        nextStep: 'Versuche es erneut. Bleibt es dabei, nenne der Administration die Angabe unten.',
        retryable: true,
      });
      setAuthDetail(
        `${error instanceof Error ? `${error.name}: ${error.message}` : String(error)} — die Anmeldung war erfolgreich; der Aufruf der Anwendung nicht.`,
      );
      setSnapshot(undefined);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    void (async () => {
      const redirect = await ready();
      const current = redirect?.account ?? msal.getAllAccounts()[0];
      if (current === undefined) {
        setBusy(false);
        return;
      }
      msal.setActiveAccount(current);
      setAccount(current);
      // Entwuerfe FREMDER Konten verschwinden, ohne gelesen zu werden.
      forgetForeignNoteDrafts(current.localAccountId);
      await load(current);
    })();
  }, [load]);

  /**
   * Liefert ein frisches Zugriffstoken fuer einen Aufruf der Anwendung.
   *
   * GETRENNT von `load`, weil die Schnellerfassung minutenlang offen stehen
   * kann: ein beim Laden geholtes Token waere beim Absenden moeglicherweise
   * abgelaufen, und die Nutzerin verloere ihre Eingabe an einem 401. `undefined`
   * heisst "die Anmeldung muss erneuert werden" und ist kein Fehler. `erneuern`
   * umgeht den Zwischenspeicher: nach einem 401 waere das gespeicherte Token
   * dasselbe abgelaufene (shell/anmeldung.ts).
   */
  const accessToken = useCallback(async (optionen?: { readonly erneuern?: boolean }): Promise<string | undefined> => {
    const current = account ?? msal.getActiveAccount() ?? undefined;
    if (current === undefined) {
      return undefined;
    }
    try {
      const result = await msal.acquireTokenSilent({
        account: current,
        scopes: [__LIAISON_API_SCOPE__],
        ...(optionen?.erneuern === true ? { forceRefresh: true } : {}),
      });
      return result.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        return undefined;
      }
      throw error;
    }
  }, [account]);

  const signIn = useCallback((): void => {
    void msal.loginRedirect({ scopes: [__LIAISON_API_SCOPE__] });
  }, []);

  const signOut = useCallback((): void => {
    // Der zuletzt gesuchte Begriff ist ein Personendatum und gehoert der
    // Identitaet, die ihn getippt hat. Der Redirect entlaedt die Anwendung
    // ohnehin; das ausdrueckliche Vergessen macht die Zusage unabhaengig vom
    // Weg der Abmeldung (Befund Codex-Erstreview 03.09.2026, ADR-0049).
    forgetSearchTerm();
    forgetBatchSelection();
    // Kein Notizentwurf darf das naechste Konto im selben Browser erreichen
    // (ADR-0051; interactions.ts).
    forgetNoteDrafts();
    void msal.logoutRedirect({ postLogoutRedirectUri: __LIAISON_REDIRECT_URI__ });
  }, []);

  const reauthenticate = useCallback((): void => {
    if (account !== undefined) {
      void msal.acquireTokenRedirect({ account, scopes: [__LIAISON_API_SCOPE__] });
    }
  }, [account]);

  // Lesende Abrufe mit mitAnmeldung() erneuern ein abgelaufenes Token still.
  useEffect(() => {
    if (account === undefined) return undefined;
    setzeAnmeldung({ accessToken, onReauthenticate: reauthenticate });
    return () => setzeAnmeldung(undefined);
  }, [account, accessToken, reauthenticate]);

  // Ohne Anmeldung gibt es genau einen Bildschirm und keine Navigation. Die
  // Adresse spielt dann keine Rolle: es gibt nichts zu adressieren.
  if (account === undefined) {
    // Waehrend der Pruefung stand hier zuvor gar kein Bildschirm -- und der
    // Rahmen zeigte folgerichtig "Seite nicht gefunden", obwohl nur die
    // Anmeldung noch lief. Ein Ladezustand ist kein fehlender Bildschirm.
    return (
      <AppShell
        screen={busy ? CHECKING_SCREEN : SIGN_IN_SCREEN}
        roles={ROLES_UNKNOWN}
        accountName={undefined}
        accountLogin={undefined}
        onSignOut={signOut}
        apiBaseUrl={__LIAISON_API_BASE_URL__}
        accessToken={accessToken}
        onReauthenticate={reauthenticate}
      >
        {busy ? <Spinner labelPosition="after" label="Anmeldung wird geprüft" /> : <SignInScreen onSignIn={signIn} />}
      </AppShell>
    );
  }

  const route = resolve(path);
  const screen = route?.screen;
  const roles: KnownRoles = snapshot?.roles ?? ROLES_UNKNOWN;

  return (
    <AppShell
      screen={screen}
      roles={roles}
      accountName={account.name ?? account.username}
      accountLogin={account.username}
      onSignOut={signOut}
      apiBaseUrl={__LIAISON_API_BASE_URL__}
      accessToken={accessToken}
      onReauthenticate={reauthenticate}
    >
      {/* Die Fehlergrenze umschliesst NUR den Bildschirm. Kopfbereich und
          Navigation bleiben stehen, wenn ein Bildschirm abstuerzt -- sonst
          waere der genannte naechste Schritt ("wechsle zu einer anderen
          Ansicht") nicht mehr ausfuehrbar. */}
      <ScreenErrorBoundary>
        {screen === undefined ? (
          <NotFoundScreen />
        ) : screen.id === 'kontakt-anlegen' ? (
          <QuickCaptureScreen
            apiBaseUrl={__LIAISON_API_BASE_URL__}
            accessToken={accessToken}
            onReauthenticate={reauthenticate}
          />
        ) : screen.id === 'kontakte' ? (
          <KontakteScreen
            apiBaseUrl={__LIAISON_API_BASE_URL__}
            accessToken={accessToken}
            onReauthenticate={reauthenticate}
          />
        ) : screen.umleitenNach !== undefined ? (
          <Umleitung nach={screen.umleitenNach} />
        ) : screen.id === 'exporte' ? (
          <MeineExporteScreen
            apiBaseUrl={__LIAISON_API_BASE_URL__}
            accessToken={accessToken}
            onReauthenticate={reauthenticate}
          />
        ) : screen.id === 'testversand' ? (
          <TestversandScreen
            apiBaseUrl={__LIAISON_API_BASE_URL__}
            accessToken={accessToken}
            onReauthenticate={reauthenticate}
          />
        ) : screen.id === 'wiedervorlagen' ? (
          <WiedervorlagenScreen
            apiBaseUrl={__LIAISON_API_BASE_URL__}
            accessToken={accessToken}
            onReauthenticate={reauthenticate}
          />
        ) : screen.id === 'sammlungen' ? (
          <SammlungenScreen
            apiBaseUrl={__LIAISON_API_BASE_URL__}
            accessToken={accessToken}
            onReauthenticate={reauthenticate}
          />
        ) : screen.id === 'sammlung' ? (
          <SammlungScreen
            apiBaseUrl={__LIAISON_API_BASE_URL__}
            accessToken={accessToken}
            onReauthenticate={reauthenticate}
            collectionId={route?.parameters.collectionId ?? ''}
          />
        ) : screen.id === 'massenpflege' ? (
          <MassenpflegeScreen
            apiBaseUrl={__LIAISON_API_BASE_URL__}
            accessToken={accessToken}
            onReauthenticate={reauthenticate}
          />
        ) : screen.id === 'datenqualitaet' ? (
          <DatenqualitaetScreen
            apiBaseUrl={__LIAISON_API_BASE_URL__}
            accessToken={accessToken}
            onReauthenticate={reauthenticate}
          />
        ) : screen.id === 'stichwortkatalog' ? (
          <StichwortkatalogScreen
            apiBaseUrl={__LIAISON_API_BASE_URL__}
            accessToken={accessToken}
            onReauthenticate={reauthenticate}
          />
        ) : screen.id === 'rollenkatalog' ? (
          <RollenkatalogScreen
            apiBaseUrl={__LIAISON_API_BASE_URL__}
            accessToken={accessToken}
            onReauthenticate={reauthenticate}
          />
        ) : screen.id === 'massenlauf' ? (
          <MassenlaufScreen
            apiBaseUrl={__LIAISON_API_BASE_URL__}
            accessToken={accessToken}
            onReauthenticate={reauthenticate}
            batchId={route?.parameters.batchId ?? ''}
          />
        ) : screen.id === 'kontakt' ? (
          <KontaktScreen
            apiBaseUrl={__LIAISON_API_BASE_URL__}
            accessToken={accessToken}
            onReauthenticate={reauthenticate}
            // Der Platzhalter aus der Adresse. Der Rahmen loest ihn auf; der
            // Bildschirm zerlegt keinen Pfad selbst -- sonst gaebe es zwei
            // Stellen, an denen die Adresse gelesen wird.
            partyId={route?.parameters.partyId ?? ''}
            // Die Entra-Objekt-ID des Kontos ist der Schluessel des
            // Notizentwurfs (ADR-0051; CM-UX-004 mit Identitaetsgrenze).
            accountId={account.localAccountId}
          />
        ) : screen.id === 'kontaktwege' ? (
          <KontaktwegeScreen
            apiBaseUrl={__LIAISON_API_BASE_URL__}
            accessToken={accessToken}
            onReauthenticate={reauthenticate}
            partyId={route?.parameters.partyId ?? ''}
          />
        ) : screen.id === 'kennungen' ? (
          <KennungenScreen
            apiBaseUrl={__LIAISON_API_BASE_URL__}
            accessToken={accessToken}
            onReauthenticate={reauthenticate}
            partyId={route?.parameters.partyId ?? ''}
          />
        ) : screen.id === 'adressen' ? (
          <AdressenScreen
            apiBaseUrl={__LIAISON_API_BASE_URL__}
            accessToken={accessToken}
            onReauthenticate={reauthenticate}
            partyId={route?.parameters.partyId ?? ''}
          />
        ) : screen.id === 'organisation' ? (
          <OrganisationScreen
            apiBaseUrl={__LIAISON_API_BASE_URL__}
            accessToken={accessToken}
            onReauthenticate={reauthenticate}
            partyId={route?.parameters.partyId ?? ''}
          />
        ) : (
          <AccessScreen
            snapshot={snapshot}
            busy={busy}
            authProblem={authProblem}
            authDetail={authDetail}
            onRetry={() => void load(account)}
            apiBaseUrl={__LIAISON_API_BASE_URL__}
            accessToken={accessToken}
          />
        )}
      </ScreenErrorBoundary>
    </AppShell>
  );
}

function Root(): ReactElement {
  // Das Thema wird HIER gewaehlt und nicht im Geruest: FluentProvider muss
  // ueber allem liegen, was Fluent-Komponenten benutzt -- die Fehlergrenze und
  // ihre Meldung eingeschlossen.
  const theme = usePrefersDark() ? liaisonDarkTheme : liaisonLightTheme;
  return (
    <FluentProvider theme={theme}>
      <App />
    </FluentProvider>
  );
}

const container = document.getElementById('root');
if (container === null) {
  throw new Error('Der Einhängepunkt #root fehlt in index.html.');
}

createRoot(container).render(
  <StrictMode>
    <Root />
  </StrictMode>,
);
